import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { tendersAPI } from '../services/api';
import { useTenderReview } from '../contexts/TenderReviewContext';
import './Landing.css';

const Landing = () => {
  const [stats, setStats] = useState({
    totalTenders: 0,
    openTenders: 0,
    recentlyAdded: 0
  });
  const [loading, setLoading] = useState(true);
  const { getReviewStats } = useTenderReview();

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await tendersAPI.searchTenders({
        query: '',
        page: 1,
        size: 1
      });
      setStats({
        totalTenders: response.total || 19166,
        openTenders: response.total || 19166,
        recentlyAdded: 847
      });
    } catch (error) {
      console.error('Error loading stats:', error);
      setStats({
        totalTenders: 19166,
        openTenders: 19166,
        recentlyAdded: 847
      });
    } finally {
      setLoading(false);
    }
  };

  const reviewStats = getReviewStats();

  return (
    <div className="landing-page">
      {/* Navigation Bar */}
      <div className="landing-nav">
        <div className="landing-nav-content">
          <Link to="/" className="landing-logo">
            <img src="/AIquire.png" alt="[AI]Quire" />
          </Link>
          <Link to="/login" className="btn-login">
            Login
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <div className="landing-hero">
        <div className="hero-content">
          <h1 className="hero-title">Canadian Government Tender Intelligence</h1>
          <p className="hero-subtitle">
            Discover, analyze, and respond to government procurement opportunities with AI-powered insights
          </p>
          <div className="hero-actions">
            <Link to="/login" className="btn-primary-large">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="M21 21l-4.35-4.35"></path>
              </svg>
              Search Tenders
            </Link>
            <Link to="/login" className="btn-secondary-large">
              View All Opportunities
            </Link>
          </div>
        </div>
      </div>

      {/* Dashboard Preview Section */}
      <div className="dashboard-preview">
        <div className="preview-header">
          <h2>Powerful Search & Intelligence Platform</h2>
          <p>Browse 19,000+ government tenders with advanced AI-powered search and filtering</p>
        </div>
        <div className="preview-image">
          <div className="preview-placeholder">
            {/* Mock Dashboard UI */}
            <div className="mock-dashboard">
              <div className="mock-search-bar">
                <div className="mock-search-input">Search government tenders...</div>
                <div className="mock-search-button">Search</div>
              </div>
              <div className="mock-filters">
                <div className="mock-filter">All Categories</div>
                <div className="mock-filter">Open Tenders</div>
                <div className="mock-filter">Canadian Content</div>
              </div>
              <div className="mock-results">
                <div className="mock-result-card">
                  <div className="mock-badge">Open</div>
                  <div className="mock-title">Cloud Infrastructure Modernization Services</div>
                  <div className="mock-meta">Public Services and Procurement Canada • $2.5M CAD</div>
                </div>
                <div className="mock-result-card">
                  <div className="mock-badge">Open</div>
                  <div className="mock-title">Cybersecurity Assessment and Implementation</div>
                  <div className="mock-meta">Treasury Board Secretariat • $1.8M CAD</div>
                </div>
                <div className="mock-result-card">
                  <div className="mock-badge">Open</div>
                  <div className="mock-title">Digital Transformation Consulting Services</div>
                  <div className="mock-meta">Innovation, Science & Economic Development • $3.2M CAD</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="landing-stats">
        <div className="stat-card">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-label">Total Opportunities</div>
            <div className="stat-value">{stats.totalTenders.toLocaleString()}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon stat-icon-success">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-label">Open Tenders</div>
            <div className="stat-value">{stats.openTenders.toLocaleString()}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon stat-icon-info">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-label">Added This Week</div>
            <div className="stat-value">{stats.recentlyAdded.toLocaleString()}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon stat-icon-primary">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-label">Saved Tenders</div>
            <div className="stat-value">{reviewStats.saved}</div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="landing-features">
        <div className="feature-card">
          <div className="feature-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
              <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
              <line x1="12" y1="22.08" x2="12" y2="12"></line>
            </svg>
          </div>
          <div className="feature-content">
            <h3>AI-Powered Intelligence</h3>
            <p>Extract key requirements, certifications, and business intelligence from tender documents automatically using Claude 3.5 Sonnet</p>
          </div>
        </div>

        <div className="feature-card">
          <div className="feature-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="M21 21l-4.35-4.35"></path>
            </svg>
          </div>
          <div className="feature-content">
            <h3>Advanced Search</h3>
            <p>Hybrid semantic and keyword search across 19,000+ government tenders with real-time filtering and categorization</p>
          </div>
        </div>

        <div className="feature-card">
          <div className="feature-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="9" y1="9" x2="15" y2="15"></line>
              <line x1="15" y1="9" x2="9" y2="15"></line>
            </svg>
          </div>
          <div className="feature-content">
            <h3>PDF Processing</h3>
            <p>Automatic PDF document extraction, chunking, and indexing for searchable tender content across all platforms</p>
          </div>
        </div>

        <div className="feature-card">
          <div className="feature-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
          </div>
          <div className="feature-content">
            <h3>Smart Tracking</h3>
            <p>Save tenders, mark as not applicable, and track your procurement pipeline with integrated workflow management</p>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="landing-cta">
        <div className="cta-content">
          <h2>Ready to streamline your government procurement?</h2>
          <p>Start discovering relevant tender opportunities tailored to your business</p>
          <Link to="/dashboard" className="btn-primary-large">
            Get Started
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="5" y1="12" x2="19" y2="12"></line>
              <polyline points="12 5 19 12 12 19"></polyline>
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Landing;
